/* Dashboard tile: Doorbell — wallet balance + Print QR + Recent rings.
   Also subscribes to the WS doorbell_ring event and pops the call dialog.
*/
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, Wallet, PhoneIncoming, PhoneMissed, Printer, Plus, Loader2, Mic, Play, Pause, Truck, Share2, Megaphone, Sparkles, NotebookPen } from "lucide-react";
import { Button } from "../ui/button";
import { useWebSocket } from "../../contexts/WebSocketContext";
import { useAuth } from "../../contexts/AuthContext";
import { subscribeToPush } from "../../utils/pushSubscription";
import OrderStickersDialog from "./OrderStickersDialog";
import ShareCampaignQrDialog from "./ShareCampaignQrDialog";
import BrandQrDialog from "./BrandQrDialog";
import AutoTopupDialog from "./AutoTopupDialog";
import QrNameDialog from "./QrNameDialog";
import CallNotesDialog from "./CallNotesDialog";
import NotesSearchDialog from "./NotesSearchDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DoorbellCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [qrToken, setQrToken] = useState(null);
  const [rings, setRings] = useState([]);
  const [showPrint, setShowPrint] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [showCampaign, setShowCampaign] = useState(false);
  const [showBrand, setShowBrand] = useState(false);
  const [showAutoTopup, setShowAutoTopup] = useState(false);
  const [autoTopup, setAutoTopup] = useState(null);
  const [showQrName, setShowQrName] = useState(false);
  const [qrName, setQrName] = useState(null);
  const [notesRing, setNotesRing] = useState(null);
  const [showNotesSearch, setShowNotesSearch] = useState(false);
  const [busy, setBusy] = useState(false);
  // Push-notification onboarding state. Kept as a plain component field
  // so re-renders reflect the browser permission (which can change if
  // the user hits "Allow" or flips the site setting). We recompute on
  // mount + every render that follows a click on the enable button.
  //
  // `pushSupported` is `false` on iOS Safari < 16.4 (no PushManager),
  // in incognito, and inside embedded webviews — the button is hidden
  // in those cases so residents don't get a broken affordance.
  const pushSupported =
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window;
  const [pushPerm, setPushPerm] = useState(
    pushSupported ? Notification.permission : "unsupported"
  );
  const [pushBusy, setPushBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [w, q, r] = await Promise.all([
        axios.get(`${API}/doorbell/wallet`, { withCredentials: true }),
        axios.post(`${API}/doorbell/qr`, {}, { withCredentials: true }),
        axios.get(`${API}/doorbell/rings?limit=5`, { withCredentials: true }),
      ]);
      setWallet(w.data);
      setQrToken(q.data.qr_token);
      setRings(r.data.rings || []);
    } catch { /* best-effort */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep link from the /demo branding showcase: `?brandqr=1` (or the
  // localStorage flag, which survives the magic-link login round-trip)
  // auto-opens the Brand-my-QR dialog once, then cleans up.
  useEffect(() => {
    let wanted = false;
    try {
      if (new URLSearchParams(window.location.search).get("brandqr") === "1") wanted = true;
      if (localStorage.getItem("guard.brandqr") === "1") { wanted = true; localStorage.removeItem("guard.brandqr"); }
    } catch { /* private mode */ }
    if (wanted) {
      setShowBrand(true);
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  // Refresh the recent rings list when any doorbell call ends, and surface
  // a small "missed call" toast so the resident sees who they missed
  // without having to glance at the recent-rings list. Only fires for
  // `reason === "missed"` to avoid noise on normal hangups.
  // Also surfaces a "Refunded" toast when the backend auto-refunds a
  // mis-click / instant-drop call (duration < 5s after answer).
  // And fires a "Voicemail from {name}" toast when the visitor leaves a
  // voice note via the auto-responder (visitor side records up to 30s
  // after 120s of no answer; backend pushes both `doorbell_voicemail`
  // and a `doorbell_end` with reason=`voicemail`).
  useWebSocket((msg) => {
    if (msg?.user_id !== user?.user_id) return;
    if (msg?.type === "doorbell_voicemail") {
      load();
      const who = msg.visitor_name || "Visitor";
      const dur = msg.duration_s ? `${msg.duration_s}s` : "";
      toast(`Voicemail from ${who}`, {
        description: dur ? `${dur} message — tap to listen.` : "Tap to listen.",
        duration: 8000,
        icon: <Mic className="w-4 h-4 text-amber-500" />,
      });
      return;
    }
    if (msg?.type !== "doorbell_end") return;
    load();
    if (msg.reason === "voicemail") {
      // The companion `doorbell_voicemail` above already toasted; just
      // make sure the rings list reloads. No extra noise.
      return;
    }
    if (msg.reason === "missed") {
      const who = msg.visitor_name || "Visitor";
      toast(`Missed call from ${who}`, {
        description: "Wallet not charged.",
        duration: 6000,
        icon: <PhoneMissed className="w-4 h-4 text-red-500" />,
      });
    } else if (msg.refunded) {
      toast("Refunded — call ended too quickly", {
        description: "₦30 returned to your wallet.",
        duration: 6000,
        icon: <Wallet className="w-4 h-4 text-emerald-500" />,
      });
    }
  });

  // Explicit user-gesture-bound push-permission request. Modern browsers
  // (especially Android Chrome) silently swallow `Notification.request-
  // Permission()` if it isn't fired from a transient user activation —
  // that's why the previous auto-prompt in useDashboardData never
  // surfaced a dialog on Android. Attaching it to this button click
  // guarantees a real gesture and unblocks Scan2Call background rings.
  //
  // On denial we don't nag: iOS/Android don't let a site re-ask once
  // the user has said no, so we hide the button and surface a hint.
  const handleEnablePush = async () => {
    if (!pushSupported || pushBusy) return;
    setPushBusy(true);
    try {
      const ok = await subscribeToPush();
      // Re-read permission; `subscribeToPush()` already called
      // `Notification.requestPermission()` under the hood.
      const perm = "Notification" in window ? Notification.permission : "denied";
      setPushPerm(perm);
      if (ok && perm === "granted") {
        toast.success("Ring notifications enabled", {
          description: "You'll get a WhatsApp-style ring even when Guard is closed.",
        });
      } else if (perm === "denied") {
        toast("Notifications blocked", {
          description:
            "Open your browser site settings for Guard and switch Notifications to Allow.",
          duration: 8000,
        });
      }
    } finally {
      setPushBusy(false);
    }
  };

  const handleTopup = useCallback(async () => {
    const amount = parseInt(window.prompt("Top up amount (NGN). Min 100:", "500"), 10);
    if (!amount || amount < 100) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/doorbell/wallet/topup/init`, { amount }, { withCredentials: true });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Top-up failed");
    } finally {
      setBusy(false);
    }
  }, []);

  // Other dashboard widgets (e.g. the Safe Route low-balance toast) can
  // trigger the top-up flow by dispatching `guard:topup`.
  useEffect(() => {
    const onTopup = () => handleTopup();
    window.addEventListener("guard:topup", onTopup);
    return () => window.removeEventListener("guard:topup", onTopup);
  }, [handleTopup]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/doorbell/wallet/auto-topup`, { withCredentials: true });
        setAutoTopup(r.data);
      } catch { /* row shows "off" */ }
      try {
        const r = await axios.get(`${API}/doorbell/display-name`, { withCredentials: true });
        setQrName(r.data);
      } catch { /* row shows placeholder */ }
    })();
  }, []);

  const visitorUrl = qrToken ? `${window.location.origin}/d/${qrToken}` : "";
  const balance = wallet?.balance_ngn ?? 0;
  const pricePerMin = wallet?.call_price_per_min_ngn || wallet?.call_price_ngn || 30;
  const minutesLeft = Math.floor(balance / pricePerMin);
  const active = balance >= pricePerMin;

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="doorbell-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell className={`w-4 h-4 ${active ? "text-emerald-600" : "text-gray-400"}`} />
            <span className="text-[10px] uppercase tracking-widest font-bold text-gray-700 font-body">Scan2Call</span>
            <span className={`inline-flex items-center gap-1 px-1.5 h-4 rounded-full text-[10px] font-bold font-body ${active ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"}`} data-testid="doorbell-status">
              <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500 animate-pulse" : "bg-gray-400"}`} aria-hidden />
              {active ? "ACTIVE" : "OFFLINE"}
            </span>
          </div>
        </div>

        <div className="mb-3">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-body">Wallet</p>
          <p className="font-heading text-3xl font-black tracking-tighter" data-testid="doorbell-balance">
            ₦{balance.toLocaleString()}
          </p>
          <p className="text-[10px] text-gray-500 font-body">~{minutesLeft} {minutesLeft === 1 ? "minute" : "minutes"} left · ₦{pricePerMin}/min</p>
          {/* Actions on their own wrapping row so none get clipped inside
              the narrow floating widget (Brand QR used to overflow). */}
          <div className="flex flex-wrap gap-2 mt-2">
            <Button onClick={handleTopup} disabled={busy} size="sm" className="bg-black hover:bg-gray-800 text-white rounded-sm font-body text-xs" data-testid="doorbell-topup-btn">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />} Top up
            </Button>
            <Button onClick={() => setShowPrint(true)} disabled={!qrToken} size="sm" variant="outline" className="rounded-sm font-body text-xs" data-testid="doorbell-print-btn">
              <Printer className="w-3 h-3 mr-1" /> Print QR
            </Button>
            <Button onClick={() => setShowBrand(true)} disabled={!qrToken} size="sm" variant="outline" className="rounded-sm font-body text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="doorbell-brand-btn">
              <Sparkles className="w-3 h-3 mr-1" /> Brand QR
            </Button>
          </div>
          <button
            onClick={() => setShowAutoTopup(true)}
            className="mt-1.5 text-[10px] font-body text-gray-500 hover:text-gray-800 underline underline-offset-2 transition-colors"
            data-testid="doorbell-auto-topup-row"
          >
            Auto top-up: {autoTopup?.enabled
              ? `below ₦${Number(autoTopup.threshold_ngn).toLocaleString()} → +₦${Number(autoTopup.amount_ngn).toLocaleString()}`
              : "off"}
          </button>
          <button
            onClick={() => setShowQrName(true)}
            className="mt-0.5 block text-[10px] font-body text-gray-500 hover:text-gray-800 underline underline-offset-2 transition-colors"
            data-testid="doorbell-qr-name-row"
          >
            QR name: {qrName ? qrName.display_name : "…"}{qrName?.custom ? " (custom)" : ""}
          </button>
          {qrName?.allowed && (
            <button
              onClick={() => setShowNotesSearch(true)}
              className="mt-0.5 block text-[10px] font-body text-gray-500 hover:text-gray-800 underline underline-offset-2 transition-colors"
              data-testid="doorbell-notes-search-row"
            >
              Search call notes
            </button>
          )}
        </div>

        {/* Low-balance nudge — under ₦100 Scan2Call goes offline and paid
            Safe Route advisories start failing. One tap opens top-up. */}
        {wallet && balance < 100 && (
          <button
            onClick={handleTopup}
            disabled={busy}
            className="w-full flex items-center justify-between gap-2 px-2 py-1.5 mb-2 rounded-sm bg-red-50 hover:bg-red-100 border border-red-200 text-red-900 text-[11px] font-body font-bold disabled:opacity-50 transition-colors"
            data-testid="doorbell-low-balance-banner"
          >
            <span className="inline-flex items-center gap-1.5">
              <Wallet className="w-3.5 h-3.5" />
              Wallet low — below ₦100 calls &amp; Safe Route can stall
            </span>
            <span className="shrink-0 underline">Top up</span>
          </button>
        )}

        {/* Enable push notifications — click-bound to satisfy Android
            Chrome's transient-user-activation requirement for
            `Notification.requestPermission()`. Only shown when the
            browser supports push AND the user hasn't already granted
            (or permanently denied) permission. Once granted the button
            hides automatically so it doesn't clutter the tile. */}
        {pushSupported && pushPerm === "default" && (
          <button
            onClick={handleEnablePush}
            disabled={pushBusy}
            className="w-full flex items-center justify-between gap-2 px-2 py-1.5 mb-2 rounded-sm bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-900 text-[11px] font-body font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="doorbell-enable-push-btn"
          >
            <span className="inline-flex items-center gap-1.5">
              {pushBusy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Bell className="w-3.5 h-3.5" />
              )}
              Enable ring notifications
            </span>
            <span className="text-[10px] uppercase tracking-widest font-bold text-amber-800/80">
              Recommended
            </span>
          </button>
        )}
        {pushSupported && pushPerm === "denied" && (
          <div
            className="w-full flex items-center gap-2 px-2 py-1.5 mb-2 rounded-sm bg-gray-50 border border-gray-200 text-gray-600 text-[11px] font-body"
            data-testid="doorbell-push-blocked"
          >
            <BellOff className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">
              Ring notifications blocked — open site settings to allow.
            </span>
          </div>
        )}

        {/* Order pre-printed weatherproof stickers — ships a 2-pack with
            the resident's QR baked in. System admins get a complimentary
            order; everyone else pays via Flutterwave. */}
        <button
          onClick={() => setShowOrder(true)}
          disabled={!qrToken}
          className="w-full flex items-center justify-between gap-2 px-2 py-1.5 mb-2 rounded-sm bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 text-[11px] font-body font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="doorbell-order-stickers-btn"
        >
          <span className="inline-flex items-center gap-1.5">
            <Truck className="w-3.5 h-3.5" />
            Order printed stickers
          </span>
          <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700/80">
            {user?.role === "system_admin" ? "Free" : "₦1,500"}
          </span>
        </button>

        {/* Campaign QR — points at the public /demo landing page rather
            than the resident's private ring URL. Existing users hand
            this out to grow the network (each new signup arrives with
            free wallet credit). */}
        <button
          onClick={() => setShowCampaign(true)}
          className="w-full flex items-center justify-between gap-2 px-2 py-1.5 mb-2 rounded-sm bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-800 text-[11px] font-body font-bold transition-colors"
          data-testid="doorbell-share-campaign-btn"
        >
          <span className="inline-flex items-center gap-1.5">
            <Megaphone className="w-3.5 h-3.5" />
            Share campaign QR
          </span>
          <span className="text-[10px] uppercase tracking-widest font-bold text-indigo-700/80">
            Grow Guard
          </span>
        </button>

        {/* Recent rings */}
        <div className="border-t border-gray-100 pt-2">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-body mb-1.5">Recent rings</p>
          {rings.length === 0 ? (
            <p className="text-xs text-gray-400 italic font-body py-1">No rings yet — print your QR and stick it to the door.</p>
          ) : (
            <ul className="space-y-1" data-testid="doorbell-rings-list">
              {rings.map((r) => {
                const answered = !!r.answered_at;
                const isVm = !!r.voicemail_id;
                const Icon = isVm ? Mic : (answered ? PhoneIncoming : PhoneMissed);
                const iconColor = isVm ? "text-amber-500" : (answered ? "text-emerald-600" : "text-red-500");
                const dur = r.duration_s ? `${Math.floor(r.duration_s / 60)}:${String(r.duration_s % 60).padStart(2, "0")}` : null;
                const time = new Date(r.started_at).toLocaleString();
                return (
                  <li key={r.ring_id} className="flex items-center justify-between text-[11px] font-body py-0.5" data-testid={`ring-${r.ring_id}`}>
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <Icon className={`w-3 h-3 shrink-0 ${iconColor}`} />
                      <span className="truncate text-gray-800">{r.visitor_name || "Visitor"}</span>
                      {isVm && <VoicemailPlayButton voicemail={r.voicemail} ringId={r.ring_id} />}
                      {qrName?.allowed && (
                        <button
                          onClick={() => setNotesRing(r)}
                          className="p-0.5 rounded-sm text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors shrink-0"
                          title="Call notes"
                          aria-label="Call notes"
                          data-testid={`ring-notes-${r.ring_id}`}
                        >
                          <NotebookPen className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {dur && <span className="text-gray-500">{dur}</span>}
                      <span className="text-gray-400 text-[10px]">{time}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {showPrint && qrToken && (
        <DoorbellPrintModal
          qrToken={qrToken}
          url={visitorUrl}
          residentName={user?.name || user?.email?.split("@")[0]}
          onClose={() => setShowPrint(false)}
        />
      )}

      <OrderStickersDialog open={showOrder} onClose={() => setShowOrder(false)} />
      <ShareCampaignQrDialog open={showCampaign} onClose={() => setShowCampaign(false)} />
      <BrandQrDialog open={showBrand} onClose={() => setShowBrand(false)} qrToken={qrToken} />
      <AutoTopupDialog open={showAutoTopup} onClose={() => setShowAutoTopup(false)} onSaved={setAutoTopup} />
      <QrNameDialog open={showQrName} onClose={() => setShowQrName(false)} onSaved={setQrName} />
      <NotesSearchDialog open={showNotesSearch} onClose={() => setShowNotesSearch(false)} />
      {notesRing && (
        <CallNotesDialog
          ringId={notesRing.ring_id}
          visitorName={notesRing.visitor_name}
          open
          onClose={() => setNotesRing(null)}
        />
      )}
    </>
  );
}

/* Voicemail row playback control. Plays /api/doorbell/voicemails/{id}/audio
   inline (the endpoint streams the original blob back with the right
   media-type so an <audio> element decodes it directly — no extra Resend /
   storage round-trip). Toggles between Play / Pause on the same row.
   Shows the duration if known. */
function VoicemailPlayButton({ voicemail, ringId }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  if (!voicemail) return null;
  const dur = voicemail.duration_s
    ? `${Math.floor(voicemail.duration_s / 60)}:${String(voicemail.duration_s % 60).padStart(2, "0")}`
    : null;
  const src = `${API}/doorbell/voicemails/${voicemail.voicemail_id}/audio`;
  const toggle = (e) => {
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      a.pause();
      setPlaying(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1 ml-1" data-testid={`voicemail-row-${ringId}`}>
      <button
        type="button"
        onClick={toggle}
        title="Play voicemail"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 hover:bg-amber-200 text-[10px] font-bold font-body"
        data-testid={`voicemail-play-${ringId}`}
      >
        {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" fill="currentColor" />}
        {dur || "VM"}
      </button>
      {/* Native HTML audio gives us decode + pause/end events for free. */}
      <audio
        ref={audioRef}
        src={src}
        preload="none"
        crossOrigin="use-credentials"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
    </span>
  );
}



/* Printable QR modal — opens browser print preview with a clean, branded
   layout designed to read clearly at door/gate distance:
     - Bold GUARD lockup at top (Shield icon + wordmark)
     - Large headline "SCAN TO GET ATTENTION"
     - High-error-correction QR (level H) at 240px so it tolerates rain/
       smudges/scratches better than a sticker would normally
     - Resident's display name below the QR so the visitor knows who they're
       about to ring
     - Footer with the visitor URL as a fallback for non-camera devices */
function DoorbellPrintModal({ qrToken, url, residentName, onClose }) {
  const handlePrint = () => window.print();

  // URL of the branded poster PNG served by the backend. Same asset the
  // Web Share flow attaches — using it here guarantees "Print QR" and
  // "Share link" always show identical, up-to-date artwork (emerald
  // rounded modules, Guard shield centered, "Ring me on Guard" caption
  // and Chime ID). Cache-busted to a 5-minute bucket so design tweaks
  // pushed by the backend appear within minutes on already-open tabs
  // without hammering the endpoint on every re-render.
  const brandedPngUrl = qrToken
    ? `${API}/doorbell/qr/${qrToken}/image.png?v=${Math.floor(Date.now() / (5 * 60 * 1000))}`
    : "";

  // "Share link" — uses the native Web Share sheet on mobile (opens
  // WhatsApp / SMS / iMessage / Twitter / etc directly) and falls back
  // to clipboard on desktop where `navigator.share` isn't wired up.
  // Same URL as the printed QR encodes so the two flows are equivalent:
  // scan the sticker OR tap a link the resident texted you.
  //
  // Extra polish: when the browser supports `navigator.canShare({files})`
  // we ALSO attach the QR sticker as a PNG so recipients on chat apps
  // see an image + link (much more shareable than a bare URL). The PNG
  // is served by `GET /api/doorbell/qr/:qr_token/image.png` — cached
  // immutable so repeat shares don't re-hit the server.
  const handleShare = async () => {
    const shareTitle = `Scan2Call — ${residentName || "Guard user"}`;
    const shareText = residentName
      ? `Reach ${residentName} live via Guard Scan2Call.`
      : "Reach me live via Guard Scan2Call.";
    const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

    // Try file-share first (image + link in a single share sheet).
    if (canNativeShare && qrToken) {
      try {
        // Cache-bust: append a rounded timestamp so residents always
        // see the latest branded poster after we push design tweaks.
        // Bucket to a 5-minute window so multiple shares in a session
        // still hit the disk cache once, but a page reload gets fresh
        // artwork. Combined with `cache: "no-cache"` this defeats the
        // year-long `immutable` header that used to pin the old PNG.
        const v = Math.floor(Date.now() / (5 * 60 * 1000));
        const pngUrl = `${API}/doorbell/qr/${qrToken}/image.png?v=${v}`;
        const res = await fetch(pngUrl, { cache: "no-cache" });
        if (res.ok) {
          const blob = await res.blob();
          const file = new File([blob], `scan2call-${qrToken}.png`, { type: "image/png" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({ title: shareTitle, text: shareText, url, files: [file] });
              return;
            } catch (e) {
              if (e?.name !== "AbortError") {
                // Fall through to URL-only share.
              } else {
                return; // user dismissed — treat as success, don't nag with clipboard toast
              }
            }
          }
        }
      } catch { /* fall through */ }
    }

    // URL-only native share.
    if (canNativeShare) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
        // fall through to clipboard
      }
    }

    // Desktop / non-supporting browsers → clipboard.
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy the link — long-press to copy manually.");
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md p-6 print:p-0 print:shadow-none print:max-w-none print:bg-white" onClick={(e) => e.stopPropagation()} data-testid="doorbell-print-modal">
        <div className="print:hidden flex justify-between items-center mb-3">
          <h3 className="font-heading text-lg font-bold tracking-tighter">Print your Scan2Call QR</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="print-close">✕</button>
        </div>

        {/* PRINT CANVAS — the branded backend PNG is the entire poster
            (Guard-logo QR + "Ring me on Guard" caption + Chime ID +
            "Scan with your phone camera" hint), so we render it as a
            single <img> instead of re-composing text around a plain
            QR canvas. Print-friendly: PNG scales cleanly, browsers
            embed <img> directly in print output, and — critically —
            the printed sticker matches exactly what recipients see on
            WhatsApp when the same resident hits "Share link". */}
        <div className="bg-white p-2 text-center font-body print:p-0">
          {brandedPngUrl ? (
            <img
              src={brandedPngUrl}
              alt={`Scan2Call QR for ${residentName || "resident"}`}
              className="block w-full max-w-[420px] mx-auto"
              data-testid="print-branded-qr"
            />
          ) : (
            <div className="py-10 text-xs text-gray-500 font-body">Loading QR…</div>
          )}
        </div>

        <div className="print:hidden flex gap-2 mt-3">
          <Button onClick={handlePrint} className="flex-1 bg-black hover:bg-gray-800 text-white rounded-sm font-body" data-testid="print-do-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
          <Button onClick={handleShare} variant="outline" className="flex-1 rounded-sm font-body" data-testid="print-share-btn">
            <Share2 className="w-4 h-4 mr-1.5" /> Share link
          </Button>
          <Button onClick={onClose} variant="outline" className="rounded-sm font-body">Close</Button>
        </div>
      </div>
    </div>
  );
}
